import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { isEmpty, isEqual } from 'lodash';

import { ProductParamInput } from 'containers/ProductRegist/hooks/types';
import { ModifyValue } from 'containers/ProductDetail/hooks/useProductDetaillHooks';
import UpdateCalendar from './UpdateCalendar';
import {
  OptionSheetType,
  useCalendarHooks,
} from 'containers/ProductRegist/components/OptionSelectRegist/hooks/useCalenderHooks';
import Text from 'components/common/Text';
import Column from 'components/common/Column';
import Row from 'components/common/Row';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import SelectDateModal from 'containers/ProductRegist/components/OptionSelectRegist/components/SelectDateModal';
import SaveButton from '../SaveButton';
import { ICON_PLUS_WHITE, ICON_PRODUCT_CHECKMARK_BLACK } from 'assets/icons';
import { Product, ProductState, ScheduleState } from 'types/api';
import HackleManager from 'utils/hackle';
import ModalHeader from './ModalHeader';
import DateAdditionalModal from 'containers/ProductRegist/components/OptionSelectRegist/components/DateAdditionalModal';
import BasicButton from 'components/Button/BasicButton';
import CommonImage from 'components/common/CommonImage';
import CalendarModifyFormV2 from './CalendarModifyFormV2';
import ConfirmDialog from 'components/Modal/ConfirmDialog';

const SelectDateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 12px;

  border-radius: 12px;
  background: #f7f7f8;
`;

const CheckedDateContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  position: sticky;
  left: 0;
  bottom: ${props => (props.$isMobile ? '100px' : '20px')};
  width: 100%;

  pointer-events: none;
  z-index: 15;
`;

interface CalendarModifyContainerIProps {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  productInfo: Product;
  modifyValue: ModifyValue;
  setModifyValue: Dispatch<SetStateAction<ModifyValue>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  handleGetProductScheduleTerm: () => void;
}

const CalendarModifyContainer: React.FC<
  CalendarModifyContainerIProps
> = props => {
  const {
    isMobile,
    productParamInput,
    productInfo,
    modifyValue,
    setModifyValue,
    handleOnChangeProductParamInput,
    handleGetProductScheduleTerm,
  } = props;

  const [copyProductParamInput, setCopyProductParamInput] =
    useState<ProductParamInput>(productParamInput);

  const isAbled = !isEqual(
    productParamInput.frip.schedules,
    copyProductParamInput.frip.schedules,
  );

  const handleOnChangeCopyProductParamInput = (key: string, value: any) => {
    setCopyProductParamInput({ ...copyProductParamInput, [key]: value });
  };

  const {
    scheduleItem,
    optionSheetType,
    checkedDate,
    isClearCalendarData,
    isOpenSelectDateModal,
    isOpenDateAdditionalModal,
    isOpenResetDialog,
    setCheckedDate,
    setIsOpenSelectDateModal,
    handleSetAddSetCheckedDate,
    handleChangeScheduleItem,
    handleClearCalenderData,
    handleResetCheckedDate,
    handleIsOpenUpdateOptionSheet,
    handleInitScheduleItem,
    handleGetSpecificDaysInRange,
    handleSetScheduleItem,
    handleCloseDateAdditionalModal,
    handleSetIsOpenResetDialog,
  } = useCalendarHooks(
    copyProductParamInput,
    handleOnChangeCopyProductParamInput,
  );

  const saveRef = useRef<HTMLDivElement>(null);
  const isAbleResetEditingSchedule = copyProductParamInput.frip.schedules.some(
    schedule => schedule.status === ScheduleState.EDITING,
  );

  const handleResetSchedules = () => {
    setCopyProductParamInput(productParamInput);
  };

  const handleResetEditingScheduleItem = () => {
    const filteredItem = copyProductParamInput.frip.schedules.filter(
      schedule => schedule.status !== ScheduleState.EDITING,
    );

    handleOnChangeProductParamInput('frip', {
      ...copyProductParamInput.frip,
      schedules: filteredItem,
    });
    handleSetIsOpenResetDialog(false);
  };

  const handleRegistSchedules = () => {
    handleOnChangeProductParamInput('frip', {
      ...productParamInput.frip,
      schedules: copyProductParamInput.frip.schedules,
    });
  };

  useEffect(() => {
    setCopyProductParamInput(productParamInput);
  }, [productParamInput]);

  useEffect(() => {
    HackleManager.logProductModifyScheduleSettingView(
      Number(productInfo.id),
      productInfo.title,
    );
  }, []);

  const renderSelectDateForm = (): JSX.Element => {
    return (
      <Row gap="12px" style={{ padding: isMobile ? '0 20px' : 0 }}>
        <SelectDateButton
          onClick={() => {
            if (checkedDate.length > 0) {
              setCheckedDate([]);
            }

            setIsOpenSelectDateModal(true);
          }}
        >
          <Text color="#414B56" weight={600}>
            날짜 일괄 선택하기
          </Text>
        </SelectDateButton>
        <SelectDateButton
          onClick={() => {
            if (isAbleResetEditingSchedule) {
              handleSetIsOpenResetDialog(true);
            }
          }}
        >
          <Text
            color={isAbleResetEditingSchedule ? '#414B56' : '#DBDCDF'}
            weight={600}
          >
            등록중 일정 초기화
          </Text>
        </SelectDateButton>
      </Row>
    );
  };

  const renderCheckedDateContainer = (): JSX.Element => {
    return (
      <CheckedDateContainer $isMobile={isMobile}>
        <BasicButton
          content="날짜 선택 해제"
          style={{
            padding: '12px 16px',
            background: '#EAEBEC',
            border: 'none',
            fontSize: '16px',
            fontWeight: 600,
            color: '#414B56',
          }}
          onClick={handleResetCheckedDate}
        />
        <BasicButton
          content={
            <Row gap="4px">
              <CommonImage src={ICON_PLUS_WHITE} />
              <Text color="#fff" size="16px" weight={600}>
                선택한 날짜에 일정 추가하기
              </Text>
            </Row>
          }
          style={{
            padding: '12px 16px',
            background: '#0071E9',
            border: 'none',
          }}
          onClick={() =>
            handleIsOpenUpdateOptionSheet({
              isOpen: true,
              sheetType: OptionSheetType.CREATE,
            })
          }
        />
      </CheckedDateContainer>
    );
  };

  const renderContents = (): JSX.Element => {
    if (isMobile) {
      return (
        <ModalContainer
          isAbled={productInfo.status !== ProductState.CLOSED && isAbled}
          isMobile={isMobile}
          onAcceptText="적용"
          onClose={() => {
            HackleManager.logProductModifyItemSaveComplete(
              Number(productInfo.id),
              productInfo.title,
              '판매 일정',
            );
            setModifyValue({ ...modifyValue, modifyStatus: '' });
          }}
          onClick={() => {
            handleRegistSchedules();

            setModifyValue({
              ...modifyValue,
              isModify: true,
              modifyStatus: '',
            });
          }}
        >
          <Column gap="8px">
            <ModalHeader
              title="판매 일정 수정하기"
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
              style={{ padding: '24px 20px' }}
            />
            <Column gap="24px" align="flex-start">
              {renderSelectDateForm()}
              <UpdateCalendar
                isMobile={isMobile}
                calendarList={copyProductParamInput.frip?.schedules}
                isClearCalendarData={isClearCalendarData}
                checkedDate={checkedDate}
                setCheckedDate={setCheckedDate}
                handleIsOpenUpdateOptionSheet={handleIsOpenUpdateOptionSheet}
                onChange={handleSetAddSetCheckedDate}
                handleInitScheduleItem={handleInitScheduleItem}
              />
            </Column>
          </Column>
          {renderUpdateCalendar()}
          {!isEmpty(checkedDate) && renderCheckedDateContainer()}
        </ModalContainer>
      );
    }

    return (
      <>
        <Row
          align="flex-start"
          style={{
            width: '100%',
            padding: '0 40px',
          }}
        >
          <Column>
            <Row
              align="center"
              justifyContent="space-between"
              style={{
                position: 'sticky',
                top: 0,
                padding: '32px 0',
                width: '100%',
                height: '100px',
                background: '#fff',
                zIndex: 10,
              }}
            >
              <Row gap="12px">
                <Text color="#1B1C1E" size="24px">
                  일정 수정하기
                </Text>
                <Text color="#989BA2" size="16px">
                  중복 선택이 가능해요
                </Text>
              </Row>
              {renderSelectDateForm()}
            </Row>
            <Column
              gap="24px"
              style={{
                padding: '0 0 32px',
              }}
            >
              <UpdateCalendar
                isMobile={isMobile}
                calendarList={copyProductParamInput.frip?.schedules}
                isClearCalendarData={isClearCalendarData}
                checkedDate={checkedDate}
                setCheckedDate={setCheckedDate}
                handleIsOpenUpdateOptionSheet={handleIsOpenUpdateOptionSheet}
                onChange={handleSetAddSetCheckedDate}
                handleInitScheduleItem={handleInitScheduleItem}
              />
              <div ref={saveRef} />
              {productInfo.status !== ProductState.CLOSED && (
                <SaveButton
                  isAbled={isAbled}
                  modifyValue={modifyValue}
                  setModifyValue={setModifyValue}
                  handleResetItem={handleResetSchedules}
                  handleRegistItem={() => {
                    HackleManager.logProductModifyItemSaveComplete(
                      Number(productInfo.id),
                      productInfo.title,
                      '판매 일정',
                    );
                    handleRegistSchedules();
                  }}
                />
              )}
            </Column>
          </Column>
          {renderUpdateCalendar()}
        </Row>
        {!isEmpty(checkedDate) && renderCheckedDateContainer()}
      </>
    );
  };

  const renderUpdateCalendar = (): JSX.Element => {
    return (
      <React.Fragment>
        {isOpenDateAdditionalModal &&
          optionSheetType === OptionSheetType.CREATE && (
            <DateAdditionalModal
              isMobile={isMobile}
              productParamInput={copyProductParamInput}
              scheduleItem={scheduleItem}
              checkedDate={checkedDate}
              optionSheetType={optionSheetType}
              isDetail={true}
              onClose={handleCloseDateAdditionalModal}
              handleSetScheduleItem={handleSetScheduleItem}
              setCheckedDate={setCheckedDate}
              handleOnChangeProductParamInput={
                handleOnChangeCopyProductParamInput
              }
              handleClearCalenderData={handleClearCalenderData}
              handleChangeScheduleItem={handleChangeScheduleItem}
              handleInitScheduleItem={handleInitScheduleItem}
            />
          )}
        {isOpenDateAdditionalModal &&
          optionSheetType === OptionSheetType.UPDATE && (
            <CalendarModifyFormV2
              isMobile={isMobile}
              productParamInput={copyProductParamInput}
              scheduleItem={scheduleItem}
              optionSheetType={optionSheetType}
              onClose={handleCloseDateAdditionalModal}
              handleSetScheduleItem={handleSetScheduleItem}
              setCheckedDate={setCheckedDate}
              handleOnChangeProductParamInput={
                handleOnChangeCopyProductParamInput
              }
              handleGetProductScheduleTerm={handleGetProductScheduleTerm}
            />
          )}
        {/* {checkedDate.length > 0 &&
          optionSheetType === OptionSheetType.CREATE && (
            <CalendarCreateForm
              productParamInput={copyProductParamInput}
              scheduleItem={scheduleItem}
              checkedDate={checkedDate}
              isMobile={isMobile}
              items={copyProductParamInput.items}
              optionSheetType={optionSheetType}
              setScheduleItem={setScheduleItem}
              setCheckedDate={setCheckedDate}
              handleOnChangeProductParamInput={
                handleOnChangeCopyProductParamInput
              }
              handleClearCalenderData={handleClearCalenderData}
              handleChangeScheduleItem={handleChangeScheduleItem}
              handleInitScheduleItem={handleInitScheduleItem}
              handleNavigateSaveButton={handleNavigateSaveButton}
            />
          )}
        {isOpenOptionBottomSheet &&
          optionSheetType === OptionSheetType.UPDATE && (
            <CalendarModifyForm
              productParamInput={copyProductParamInput}
              scheduleItem={scheduleItem}
              checkedDate={checkedDate}
              isMobile={isMobile}
              items={copyProductParamInput.items}
              optionSheetType={optionSheetType}
              setScheduleItem={setScheduleItem}
              setCheckedDate={setCheckedDate}
              handleOnChangeProductParamInput={
                handleOnChangeCopyProductParamInput
              }
              handleClearCalenderData={handleClearCalenderData}
              handleChangeScheduleItem={handleChangeScheduleItem}
              handleInitScheduleItem={handleInitScheduleItem}
              handleOnCloseOptionModal={() => setIsOpenOptionBottomSheet(false)}
              handleGetProductScheduleTerm={handleGetProductScheduleTerm}
              handleNavigateSaveButton={handleNavigateSaveButton}
            />
          )} */}
        {isOpenSelectDateModal && (
          <SelectDateModal
            isMobile={isMobile}
            onClose={() => setIsOpenSelectDateModal(false)}
            handleGetSpecificDaysInRange={handleGetSpecificDaysInRange}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      {renderContents()}
      {isOpenResetDialog && (
        <ConfirmDialog
          title="등록중 일정을 초기화할까요?"
          description="일정은 저장되지 않고 삭제됩니다."
          onAcceptText="네, 초기화 할게요"
          onCloseText="취소"
          onClose={() => {
            handleSetIsOpenResetDialog(false);
          }}
          onAccept={handleResetEditingScheduleItem}
        />
      )}
    </>
  );
};

export default CalendarModifyContainer;
